import { useEffect, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Image, Plus, Trash2 } from 'lucide-react'

import { DashboardLayout } from '../../components/layout/DashboardLayout'
import { Breadcrumb } from '../../components/ui/Breadcrumb'
import { shellAssets } from '../../constants/assets'
import { dashboardRoutes } from '../../constants/routes'
import { fleets } from '../../data/services'
import type { FleetRecord } from '../../types'
import { USE_DASHBOARD_REAL_BACKEND } from '../../api/config/config'
import {
  apiCreateFleet,
  apiGetFleet,
  apiUpdateFleet,
} from '../../api/services/fleetApi'

import {
  apiGetVehicleCategories,
  type VehicleCategory,
} from '../../api/vehicleManagement/vehicleCategoryApi'

import {
  apiGetVehicleBrands,
  type VehicleBrand,
} from '../../api/vehicleManagement/vehicleBrandApi'

const currentUser = {
  name: 'Admin',
  email: 'Admin@123gmail.com',
  avatar: shellAssets.avatar,
}

const defaultFeaturesList: string[] = []

export const FleetFormPage = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditing = Boolean(id)

  const [categories, setCategories] = useState<VehicleCategory[]>([])
  const [brands, setBrands] = useState<VehicleBrand[]>([])

  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(true)

  const [availableFeatures, setAvailableFeatures] =
    useState(defaultFeaturesList)
  const [isRemoving, setIsRemoving] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const [newFeature, setNewFeature] = useState('')

  const [formData, setFormData] = useState({
    name: '',
    categoryId: null as number | null,
    brandId: null as number | null,
    registration: '',
    passengers: '',
    suitcases: '',
    cabinBags: '',
    features: [] as string[],
    description: '',

    basePrice: '',
    dayTripPrice: '',
    hourlyPrice: '',
    pricePerKm: '',
    minimumBookingHours: '1',
    displayOrder: '0',

    active: true,
    image: '',
    imageFile: null as File | null,
  })

  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const loadDropdownData = async () => {
      setIsLoadingDropdowns(true)

      try {
        const [categoryResult, brandResult] = await Promise.all([
          apiGetVehicleCategories(),
          apiGetVehicleBrands(),
        ])

        if (categoryResult.success && categoryResult.data) {
          setCategories(categoryResult.data)
        } else {
          console.error(
            'Failed to load vehicle categories:',
            categoryResult.message,
          )
        }

        if (brandResult.success && brandResult.data) {
          setBrands(brandResult.data)
        } else {
          console.error(
            'Failed to load vehicle brands:',
            brandResult.message,
          )
        }
      } catch (error) {
        console.error(
          'Failed to load vehicle categories and brands:',
          error,
        )
      } finally {
        setIsLoadingDropdowns(false)
      }
    }

    loadDropdownData()
  }, [])

  useEffect(() => {
    if (!isEditing) {
      return
    }

    const populateForm = (fleet: FleetRecord) => {
      if (fleet) {
        setFormData({
          name: fleet.name,
          categoryId: fleet.categoryId,
          brandId: fleet.brandId || 0,
          registration: fleet.plate || '',
          passengers: String(fleet.passengers),
          suitcases: String(fleet.luggage),
          cabinBags: String(fleet.cabinBags || fleet.luggage),
          features: fleet.features || [],
          description: fleet.description,

          basePrice:
            fleet.basePrice !== undefined
              ? String(fleet.basePrice)
              : '0.00',

          dayTripPrice:
            fleet.dayTripPrice !== undefined
              ? String(fleet.dayTripPrice)
              : '0.00',

          hourlyPrice:
            fleet.hourlyPrice !== undefined
              ? String(fleet.hourlyPrice)
              : '0.00',

          pricePerKm:
            fleet.pricePerKm !== undefined
              ? String(fleet.pricePerKm)
              : '0.00',

          minimumBookingHours:
            fleet.minimumBookingHours !== undefined
              ? String(fleet.minimumBookingHours)
              : '1',

          displayOrder:
            fleet.displayOrder !== undefined
              ? String(fleet.displayOrder)
              : '0',

          active: fleet.active,
          image: fleet.image || '',
          imageFile: null,
        })

        // Merge any custom features from the fleet into availableFeatures
        if (fleet.features) {
          setAvailableFeatures((prev) => {
            const newFeats = fleet.features!.filter(
              (f) => !prev.includes(f),
            )

            return [...prev, ...newFeats]
          })
        }
      }
    }

    if (USE_DASHBOARD_REAL_BACKEND) {
      apiGetFleet(id!).then((result) => {
        if (result.success && result.data) {
          populateForm(result.data)
        } else {
          console.error('Failed to load fleet:', result.message)
        }
      })

      return
    }

    const fleet = fleets.find((record) => record.id === id)

    if (fleet) {
      populateForm(fleet)
    }
  }, [id, isEditing])

  const handleSave = async () => {
    if (USE_DASHBOARD_REAL_BACKEND) {
      const payload = {
        name: formData.name,
        categoryId: formData.categoryId || 0,
        brandId: formData.brandId || 0,

        registration: formData.registration,

        passengers: parseInt(formData.passengers) || 0,
        suitcases: parseInt(formData.suitcases) || 0,
        cabinBags: parseInt(formData.cabinBags) || 0,

        features: formData.features,
        description: formData.description,

        basePrice: parseFloat(formData.basePrice) || 0,
        dayTripPrice: parseFloat(formData.dayTripPrice) || 0,
        hourlyPrice: parseFloat(formData.hourlyPrice) || 0,
        pricePerKm: parseFloat(formData.pricePerKm) || 0,

        minimumBookingHours:
          parseInt(formData.minimumBookingHours) || 1,

        displayOrder:
          parseInt(formData.displayOrder) || 0,

        active: formData.active,
        imageFile: formData.imageFile,
      }

      console.log('Payload:', payload)

      const result = isEditing
        ? await apiUpdateFleet(id!, payload)
        : await apiCreateFleet(payload)

      if (!result.success) {
        window.alert(result.message)
        return
      }

      navigate(dashboardRoutes.services)
      return
    }

    if (isEditing) {
      const index = fleets.findIndex((f) => f.id === id)

      if (index !== -1) {
        fleets[index] = {
          ...fleets[index],

          name: formData.name,
          categoryId: formData.categoryId,
          brandId: formData.brandId,

          plate: formData.registration,

          passengers: parseInt(formData.passengers) || 0,
          luggage: parseInt(formData.suitcases) || 0,
          cabinBags: parseInt(formData.cabinBags) || 0,

          features: formData.features,
          description: formData.description,

          basePrice: parseFloat(formData.basePrice) || 0,
          dayTripPrice:
            parseFloat(formData.dayTripPrice) || 0,
          hourlyPrice:
            parseFloat(formData.hourlyPrice) || 0,
          pricePerKm: parseFloat(formData.pricePerKm) || 0,

          minimumBookingHours:
            parseInt(formData.minimumBookingHours) || 1,

          displayOrder:
            parseInt(formData.displayOrder) || 0,

          active: formData.active,

          ...(formData.image
            ? { image: formData.image }
            : {}),
        }
      }
    } else {
      const newFleet: FleetRecord = {
        id: `f-${Date.now()}`,

        categoryId: formData.categoryId || 0,
        name: formData.name || 'New Vehicle',
        brandId: formData.brandId || 0,

        plate: formData.registration || '',

        image:
          formData.image ||
          '/assets/images/fleet-passat.png',

        passengers:
          parseInt(formData.passengers) || 0,

        luggage:
          parseInt(formData.suitcases) || 0,

        cabinBags:
          parseInt(formData.cabinBags) || 0,

        features: formData.features,
        description: formData.description,

        basePrice:
          parseFloat(formData.basePrice) || 0,

        dayTripPrice:
          parseFloat(formData.dayTripPrice) || 0,

        hourlyPrice:
          parseFloat(formData.hourlyPrice) || 0,

        pricePerKm:
          parseFloat(formData.pricePerKm) || 0,

        minimumBookingHours:
          parseInt(formData.minimumBookingHours) || 1,

        displayOrder:
          parseInt(formData.displayOrder) || 0,

        active: formData.active,
      }

      fleets.push(newFleet)
    }

    navigate(dashboardRoutes.services)
  }

  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0]

    if (file) {
      const url = URL.createObjectURL(file)

      setFormData((prev) => ({
        ...prev,
        image: url,
        imageFile: file,
      }))
    }
  }

  const handleFeatureToggle = (feature: string) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter((f) => f !== feature)
        : [...prev.features, feature],
    }))
  }

  const handleDeleteFeature = (feature: string) => {
    setAvailableFeatures((prev) =>
      prev.filter((f) => f !== feature),
    )

    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((f) => f !== feature),
    }))
  }

  const handleAddNewFeature = () => {
    const trimmed = newFeature.trim()

    if (
      trimmed &&
      !availableFeatures.includes(trimmed)
    ) {
      setAvailableFeatures((prev) => [
        ...prev,
        trimmed,
      ])

      setFormData((prev) => ({
        ...prev,
        features: [...prev.features, trimmed],
      }))
    }

    setNewFeature('')
    setIsAdding(false)
  }

  return (
    <DashboardLayout
      activeNavId="services"
      contentGap={15}
      subtitle={
        <Breadcrumb
          items={[
            {
              label: 'Dashboard',
              path: dashboardRoutes.overview,
            },
            {
              label: 'Services',
              path: dashboardRoutes.services,
            },
            {
              label: isEditing
                ? 'Edit Fleet Vehicle'
                : 'Add New Fleet Vehicle',
            },
          ]}
        />
      }
      title={
        isEditing
          ? 'Edit Fleet Vehicle'
          : 'Add New Fleet Vehicle'
      }
      user={currentUser}
    >
      <div className="flex flex-col items-start gap-6 lg:flex-row">
        {/* Left Column */}
        <div className="flex w-full flex-col gap-6 rounded-[16px] bg-white p-[24px] shadow-sm lg:w-[65%]">
          <h2 className="font-host text-[18px] font-bold text-[#1A1A1A]">
            Vehicle Information
          </h2>

          {/* Vehicle Info Grid */}
          <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label className="font-host text-[13px] font-medium text-[#1A1A1A]">
                Vehicle Name
              </label>

              <input
                type="text"
                placeholder="Enter vehicle"
                value={formData.name}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    name: e.target.value,
                  })
                }
                className="h-[44px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:border-[#FF6A1A] focus:outline-none focus:ring-1 focus:ring-[#FF6A1A]"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="font-host text-[13px] font-medium text-[#1A1A1A]">
                Vehicle Category
              </label>

              <select
                value={formData.categoryId ?? ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    categoryId: Number(e.target.value),
                  })
                }
                disabled={isLoadingDropdowns}
                className="h-[44px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A]"
              >
                <option value="">
                  {isLoadingDropdowns
                    ? 'Loading categories...'
                    : 'Select category'}
                </option>

                {categories
                  .filter((category) => category.is_active)
                  .map((category) => (
                    <option
                      key={category.id}
                      value={category.id}
                    >
                      {category.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="font-host text-[13px] font-medium text-[#1A1A1A]">
                Vehicle Brand
              </label>

              <select
                value={formData.brandId ?? ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    brandId: Number(e.target.value),
                  })
                }
                disabled={isLoadingDropdowns}
                className="h-[44px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A]"
              >
                <option value="">
                  {isLoadingDropdowns
                    ? 'Loading brands...'
                    : 'Select brand'}
                </option>

                {brands
                  .filter((brand) => brand.is_active)
                  .map((brand) => (
                    <option
                      key={brand.id}
                      value={brand.id}
                    >
                      {brand.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="font-host text-[13px] font-medium text-[#1A1A1A]">
                Registration Number (Optional)
              </label>

              <input
                type="text"
                placeholder="Enter registration"
                value={formData.registration}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    registration: e.target.value,
                  })
                }
                className="h-[44px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:border-[#FF6A1A] focus:outline-none focus:ring-1 focus:ring-[#FF6A1A]"
              />
            </div>
          </div>

          {/* Capacity Grid */}
          <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <label className="font-host text-[13px] font-medium text-[#1A1A1A]">
                Maximum Passengers
              </label>

              <input
                type="number"
                min="1"
                placeholder="0"
                value={formData.passengers}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    passengers: e.target.value,
                  })
                }
                className="h-[44px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:border-[#FF6A1A] focus:outline-none focus:ring-1 focus:ring-[#FF6A1A]"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="font-host text-[13px] font-medium text-[#1A1A1A]">
                Maximum Suitcases
              </label>

              <input
                type="number"
                min="0"
                placeholder="0"
                value={formData.suitcases}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    suitcases: e.target.value,
                  })
                }
                className="h-[44px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:border-[#FF6A1A] focus:outline-none focus:ring-1 focus:ring-[#FF6A1A]"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="font-host text-[13px] font-medium text-[#1A1A1A]">
                Maximum Cabin Bags
              </label>

              <input
                type="number"
                min="0"
                placeholder="0"
                value={formData.cabinBags}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    cabinBags: e.target.value,
                  })
                }
                className="h-[44px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:border-[#FF6A1A] focus:outline-none focus:ring-1 focus:ring-[#FF6A1A]"
              />
            </div>
          </div>

          {/* Features and amenities */}
          <div className="mt-2">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-host text-[16px] font-bold text-[#1A1A1A]">
                Features and amenities
              </h2>

              <div className="flex items-center gap-3">
                {!isRemoving && !isAdding && (
                  <button
                    type="button"
                    onClick={() => setIsAdding(true)}
                    className="flex items-center gap-1.5 rounded-[6px] bg-[#FFE8E0] px-3 py-1.5 text-[12px] font-semibold text-[#FF6A1A] transition-colors hover:bg-[#FFD3C2]"
                  >
                    <Plus size={14} strokeWidth={2.5} />
                    Add Highlight
                  </button>
                )}

                {isAdding && (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newFeature}
                      onChange={(e) =>
                        setNewFeature(e.target.value)
                      }
                      onKeyDown={(e) =>
                        e.key === 'Enter' &&
                        handleAddNewFeature()
                      }
                      placeholder="New feature..."
                      autoFocus
                      className="h-[28px] rounded-[4px] border border-[#E0E0E0] px-2 text-[12px] focus:border-[#FF6A1A] focus:outline-none"
                    />

                    <button
                      type="button"
                      onClick={handleAddNewFeature}
                      className="rounded-[4px] bg-[#FF6A1A] px-2 py-1 text-[12px] font-semibold text-white"
                    >
                      Add
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setIsAdding(false)
                        setNewFeature('')
                      }}
                      className="text-[12px] text-[#757575] hover:text-[#1A1A1A]"
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {!isAdding && (
                  <button
                    type="button"
                    onClick={() =>
                      setIsRemoving(!isRemoving)
                    }
                    className={`flex items-center gap-1.5 rounded-[6px] px-3 py-1.5 text-[12px] font-semibold transition-colors ${
                      isRemoving
                        ? 'bg-[#E0E0E0] text-[#1A1A1A] hover:bg-[#D6D6D6]'
                        : 'bg-[#F5F5F5] text-[#1A1A1A] hover:bg-[#E0E0E0]'
                    }`}
                  >
                    {!isRemoving && <Trash2 size={14} />}
                    {isRemoving ? 'Done' : 'Remove'}
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 md:grid-cols-3">
              {availableFeatures.map((feature) => (
                <div
                  key={feature}
                  className="flex items-center justify-between"
                >
                  <label className="flex cursor-pointer items-center gap-3">
                    <input
                      type="checkbox"
                      checked={formData.features.includes(feature)}
                      onChange={() =>
                        handleFeatureToggle(feature)
                      }
                      disabled={isRemoving}
                      className="size-5 cursor-pointer rounded-[4px] border-[#E0E0E0] text-[#FF6A1A] focus:ring-[#FF6A1A] disabled:opacity-50"
                    />

                    <span
                      className={`font-host text-[14px] ${
                        isRemoving
                          ? 'text-[#757575]'
                          : 'text-[#1A1A1A]'
                      }`}
                    >
                      {feature}
                    </span>
                  </label>

                  {isRemoving && (
                    <button
                      type="button"
                      onClick={() =>
                        handleDeleteFeature(feature)
                      }
                      className="text-[#FF6A1A] transition-colors hover:text-red-600"
                      title="Remove from list"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Short Description */}
          <div className="mt-4 flex flex-col gap-1.5">
            <label className="font-host text-[14px] font-bold text-[#1A1A1A]">
              Short Description
            </label>

            <textarea
              placeholder="Describe this service..."
              value={formData.description}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  description: e.target.value,
                })
              }
              maxLength={180}
              className="min-h-[100px] resize-none rounded-[8px] border border-[#E0E0E0] bg-white p-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:border-[#FF6A1A] focus:outline-none focus:ring-1 focus:ring-[#FF6A1A]"
            />

            <span className="font-host text-[11px] text-[#9E9E9E]">
              (Max 180 Characters)
            </span>
          </div>
        </div>

        {/* Right Column */}
        <div className="flex w-full flex-col gap-6 lg:w-[35%]">
          {/* Vehicle Image Upload */}
          <div className="flex flex-col gap-3 rounded-[16px] bg-white p-[24px] shadow-sm">
            <h2 className="font-host text-[16px] font-bold text-[#1A1A1A]">
              Vehicle Image
            </h2>

            <div
              className="group relative flex h-[200px] w-full cursor-pointer flex-col items-center justify-center overflow-hidden rounded-[12px] border-2 border-dashed border-[#B2D873] bg-[#F9FBE7] transition-colors hover:bg-[#F1F8E9]"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*"
                onChange={handleImageUpload}
              />

              {formData.image ? (
                <>
                  <img
                    src={formData.image}
                    alt="Vehicle preview"
                    className="h-full w-full object-contain p-2"
                  />

                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                    <span className="font-host font-semibold text-white">
                      Change Image
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-4 flex size-[48px] items-center justify-center rounded-[12px] bg-[#1A1A1A]">
                    <Image
                      className="text-white"
                      size={24}
                    />
                  </div>

                  <span className="font-host text-[14px] text-[#555555]">
                    Drag & Drop images or
                  </span>

                  <span className="font-host text-[14px] font-semibold text-[#1E88E5]">
                    Browse Files
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Pricing */}
          <div className="flex flex-col gap-4 rounded-[16px] bg-white p-[24px] shadow-sm">
            <h2 className="font-host text-[16px] font-bold text-[#1A1A1A]">
              Pricing
            </h2>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
              {/* Base Price */}
              <div className="flex flex-col gap-1.5 rounded-[8px] border border-[#E0E0E0] p-3">
                <span className="font-host text-[12px] text-[#757575]">
                  Base Price (€)
                </span>

                <div className="flex items-center gap-1 font-host text-[15px] font-medium text-[#1A1A1A]">
                  €
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.basePrice}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        basePrice: e.target.value,
                      })
                    }
                    className="w-full bg-transparent focus:outline-none"
                  />
                </div>
              </div>

              {/* Day Trip Price */}
              <div className="flex flex-col gap-1.5 rounded-[8px] border border-[#E0E0E0] p-3">
                <span className="font-host text-[12px] text-[#757575]">
                  Day Trip Price (€)
                </span>

                <div className="flex items-center gap-1 font-host text-[15px] font-medium text-[#1A1A1A]">
                  €
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.dayTripPrice}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        dayTripPrice: e.target.value,
                      })
                    }
                    className="w-full bg-transparent focus:outline-none"
                  />
                </div>
              </div>

              {/* Hourly Price */}
              <div className="flex flex-col gap-1.5 rounded-[8px] border border-[#E0E0E0] p-3">
                <span className="font-host text-[12px] text-[#757575]">
                  Hourly Price (€)
                </span>

                <div className="flex items-center gap-1 font-host text-[15px] font-medium text-[#1A1A1A]">
                  €
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.hourlyPrice}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        hourlyPrice: e.target.value,
                      })
                    }
                    className="w-full bg-transparent focus:outline-none"
                  />
                </div>
              </div>

              {/* Price per KM */}
              <div className="flex flex-col gap-1.5 rounded-[8px] border border-[#E0E0E0] p-3">
                <span className="font-host text-[12px] text-[#757575]">
                  Price per KM (€)
                </span>

                <div className="flex items-center gap-1 font-host text-[15px] font-medium text-[#1A1A1A]">
                  €
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.pricePerKm}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        pricePerKm: e.target.value,
                      })
                    }
                    className="w-full bg-transparent focus:outline-none"
                  />
                </div>
              </div>

              {/* Minimum Booking Hours */}
              <div className="flex flex-col gap-1.5 rounded-[8px] border border-[#E0E0E0] p-3 sm:col-span-2 lg:col-span-1 xl:col-span-2">
                <span className="font-host text-[12px] text-[#757575]">
                  Minimum Booking Hours
                </span>

                <input
                  type="number"
                  min="1"
                  step="1"
                  value={formData.minimumBookingHours}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      minimumBookingHours: e.target.value,
                    })
                  }
                  className="w-full bg-transparent font-host text-[15px] font-medium text-[#1A1A1A] focus:outline-none"
                />

                <span className="font-host text-[10px] text-[#9E9E9E]">
                  Minimum duration required for this vehicle
                </span>
              </div>

              {/* Display Order */}
              <div className="flex flex-col gap-1.5 rounded-[8px] border border-[#E0E0E0] p-3 sm:col-span-2 lg:col-span-1 xl:col-span-2">
                <span className="font-host text-[12px] text-[#757575]">
                  Display Order
                </span>

                <input
                  type="number"
                  min="0"
                  step="1"
                  value={formData.displayOrder}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      displayOrder: e.target.value,
                    })
                  }
                  className="w-full bg-transparent font-host text-[15px] font-medium text-[#1A1A1A] focus:outline-none"
                />

                <span className="font-host text-[10px] text-[#9E9E9E]">
                  Lower numbers appear first in the fleet list
                </span>
              </div>
            </div>
          </div>

          {/* Availability */}
          <div className="flex flex-col gap-3 rounded-[16px] bg-white p-[24px] shadow-sm">
            <h2 className="font-host text-[16px] font-bold text-[#1A1A1A]">
              Availability
            </h2>

            <span className="font-host text-[13px] text-[#757575]">
              Service Status
            </span>

            <label className="flex w-fit cursor-pointer items-center gap-3 rounded-[8px] border border-[#8BC34A] bg-[#F1F8E9] px-4 py-2.5">
              <div
                className={`flex size-5 items-center justify-center rounded-full border-[6px] ${
                  formData.active
                    ? 'border-[#8BC34A] bg-white'
                    : 'border-[#E0E0E0] bg-white'
                }`}
              />

              <span className="font-host text-[14px] font-bold text-[#1A1A1A]">
                Active
              </span>

              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    active: e.target.checked,
                  })
                }
                className="hidden"
              />
            </label>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="mt-8 flex items-center justify-between pb-8">
        <button
          type="button"
          onClick={() =>
            navigate(dashboardRoutes.services)
          }
          className="flex h-[44px] items-center justify-center rounded-[8px] border border-[#E0E0E0] bg-white px-[48px] font-host text-[15px] font-bold text-[#1A1A1A] transition-colors hover:bg-gray-50"
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={handleSave}
          className="flex h-[44px] items-center justify-center rounded-[8px] bg-[#334E1C] px-[48px] font-host text-[15px] font-bold text-white transition-colors hover:bg-[#253914]"
        >
          {isEditing ? 'Save Changes' : 'Create Service'}
        </button>
      </div>
    </DashboardLayout>
  )
}